/**
 * TopUpModal - Modal de confirmation pour top-up ESSENTIAL et PLUS
 * 
 * Modal spécialisée pour permettre aux utilisateurs ESSENTIAL et PLUS de recharger
 * des crédits supplémentaires via Stripe.
 * 
 * - ESSENTIAL : Top-up standard pour acheter plus de crédits
 * - PLUS : Top-up d'urgence si crédits mensuels épuisés avant renouvellement
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PlansService from '../../services/api/plansService';

interface SystemConfig {
    topup_essential_credits: number;
    topup_essential_price: number;
}

interface TopUpModalProps {
    id: string;
    config: SystemConfig;
    currentCredits: number;
    companyId?: string; // Optionnel si onTopUpRequest fourni
    accessToken?: string; // Optionnel si onTopUpRequest fourni
    onClose?: () => void;
    /** Entreprise SSO Microsoft : achat via Azure Marketplace au lieu de Stripe */
    useMicrosoftMarketplace?: boolean;
    marketplaceUrl?: string;
    /**
     * Callback pour gérer le paiement top-up (optionnel, sinon utilise PlansService directement)
     * @param topupCredits - Nombre de crédits à ajouter
     * @returns Promise<void>
     */
    onTopUpRequest?: (topupCredits: number) => Promise<void>;
}

const TopUpModal: React.FC<TopUpModalProps> = ({
    id,
    config,
    currentCredits,
    companyId,
    accessToken,
    onClose,
    useMicrosoftMarketplace = false,
    marketplaceUrl,
    onTopUpRequest
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const handleTopUp = async () => {
        try {
            if (useMicrosoftMarketplace && marketplaceUrl?.trim()) {
                window.open(marketplaceUrl.trim(), '_blank', 'noopener,noreferrer');
                if (closeRef.current) {
                    closeRef.current.click();
                }
                onClose?.();
                return;
            }

            // Priorité 1: Utiliser callback fourni par parent (pour compatibilité Dashboard)
            if (onTopUpRequest) {
                await onTopUpRequest(config.topup_essential_credits);
            }
            // Priorité 2: Utiliser PlansService directement (mode autonome)
            else if (companyId && accessToken) {
                console.log('[TopUpModal] Using PlansService.purchaseTopUp for', config.topup_essential_credits, 'credits');
                const response = await PlansService.purchaseTopUp(
                    companyId,
                    config.topup_essential_credits,
                    accessToken
                );
                
                if (response.success && response.data?.session_url) {
                    console.log('[TopUpModal] Redirecting to Stripe Checkout:', response.data.session_url);
                    // Rediriger vers Stripe Checkout
                    window.location.href = response.data.session_url;
                } else {
                    const errorMsg = PlansService.formatErrorMessage(response);
                    alert(`Erreur lors du top-up: ${errorMsg}`);
                    return; // Ne pas fermer le modal en cas d'erreur
                }
            } else {
                console.error('[TopUpModal] Missing companyId or accessToken');
                alert('Erreur: informations manquantes pour le top-up');
                return;
            }
            
            // Fermer le modal après initiation du paiement réussie
            if (closeRef.current) {
                closeRef.current.click();
            }
            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error('[TopUpModal] Top-up request error:', error);
            alert('Erreur lors du top-up. Veuillez réessayer.');
        }
    };

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
        document.body.style.overflow = '';
    }, [onClose]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const handleModalHidden = () => {
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            document.body.style.overflow = 'hidden';

            // Ouvrir le modal automatiquement
            const bootstrap = (window as any).bootstrap;
            if (bootstrap) {
                const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
                modal.show();
            }

            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                document.body.style.overflow = '';
            };
        }
    }, [handleClose]);

    const newTotalCredits = currentCredits + config.topup_essential_credits;

    const modalContent = (
        <div
            ref={modalRef}
            className="modal fade"
            data-bs-backdrop="true"
            data-bs-keyboard="true"
            id={id}
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 10001, pointerEvents: 'auto' }}>
                <div className="modal-content" style={{ zIndex: 10002, pointerEvents: 'auto', backgroundColor: '#1a1a1a' }}>
                    <div className="modal-header border-secondary">
                        <h5 className="modal-title text-theme" id={`${id}Label`}>
                            <i className="fa-solid fa-plus-circle me-2"></i>
                            {t('views.modal.topUp.title', 'Top Up Credits')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body text-white">
                        {/* Solde actuel */}
                        <div className="mb-4">
                            <h6 className="text-theme">{t('views.modal.topUp.currentBalance', 'Current Balance')}</h6>
                            <div className="p-3 bg-dark border border-secondary rounded">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>{t('views.modal.topUp.credits', 'Available Credits')}:</span>
                                    <span className="fs-4 fw-bold text-info">
                                        {currentCredits}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Détails du top-up */}
                        <div className="mb-4">
                            <h6 className="text-theme">{t('views.modal.topUp.packageDetails', 'Top-up Package Details')}</h6>
                            <div className="p-3 bg-dark border border-success rounded">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{t('views.modal.topUp.package', 'Credits Package')}:</span>
                                    <strong className="text-success">
                                        +40 {t('views.pricing.units.credits', 'credits')}
                                        {/* +{config.topup_essential_credits} {t('views.pricing.units.credits', 'credits')} */}
                                    </strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{t('views.modal.topUp.price', 'Price')}:</span>
                                    <strong className="text-warning">
                                        ${config.topup_essential_price}
                                    </strong>
                                </div>
                                <hr className="border-secondary" />
                                <div className="d-flex justify-content-between">
                                    <span className="fw-bold">{t('views.modal.topUp.newTotal', 'New Total Balance')}:</span>
                                    <strong className="text-theme fs-5">
                                        {newTotalCredits} {t('views.pricing.units.credits', 'crédits')}
                                    </strong>
                                </div>
                            </div>
                        </div>

                        {/* Informations sur le top-up */}
                        <div className="alert alert-info mb-4">
                            <h6 className="mb-2">
                                <i className="fa-solid fa-info-circle me-2"></i>
                                {t('views.modal.topUp.howItWorks', 'How it works?')}
                            </h6>
                            <ul className="mb-0 small">
                                <li>{t('views.modal.topUp.info1', 'Credits are added immediately to your balance')}</li>
                                <li>{t('views.modal.topUp.info2', 'No expiration, use them whenever you want')}</li>
                                {/* <li>{t('views.modal.topUp.info3', 'You stay on your current ESSENTIAL plan')}</li> */}
                                <li>
                                    {useMicrosoftMarketplace
                                        ? t('views.modal.topUp.info4Microsoft')
                                        : t('views.modal.topUp.info4')}
                                </li>
                            </ul>
                        </div>

                        {/* Avantages */}
                        {/* <div className="alert alert-success">
                            <h6 className="mb-2">
                                <i className="fa-solid fa-check-circle me-2"></i>
                                {t('views.modal.topUp.benefits', 'With these credits, you\'ll be able to')}:
                            </h6>
                            <ul className="mb-0 small">
                                <li>
                                    <strong>{config.topup_essential_credits}</strong> {t('views.modal.topUp.benefit1', 'new processes to generate')}
                                </li>
                                <li>
                                    <strong>~{config.topup_essential_credits * 5000}</strong> {t('views.modal.topUp.benefit2', 'document analysis tokens')}
                                </li>
                                <li>{t('views.modal.topUp.benefit3', 'Access to all ESSENTIAL features')}</li>
                            </ul>
                        </div> */}
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            {t('words.cancel', 'Annuler')}
                        </button>
                        <button
                            type="button"
                            onClick={handleTopUp}
                            className="btn btn-success"
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            <i className={`fa-solid ${useMicrosoftMarketplace ? 'fa-store' : 'fa-credit-card'} me-2`}></i>
                            {useMicrosoftMarketplace
                                ? t('views.modal.topUp.purchaseViaAzure')
                                : `${t('views.modal.topUp.purchase')} $${config.topup_essential_price}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default TopUpModal;
